import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Lock, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API, LanguageToggle } from '../App';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const ResetPasswordPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(i18n.language === 'el' ? 'Οι κωδικοί δεν ταιριάζουν' : 'Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error(i18n.language === 'el' ? 'Ο κωδικός πρέπει να είναι τουλάχιστον 6 χαρακτήρες' : 'Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-misel-bg flex items-center justify-center p-8">
        <div className="text-center">
          <h1 className="font-roboto font-bold text-2xl text-white mb-4">
            {i18n.language === 'el' ? 'Μη έγκυρος σύνδεσμος' : 'Invalid Link'}
          </h1>
          <Link to="/forgot-password">
            <Button className="bg-primary hover:bg-primary/90">
              {i18n.language === 'el' ? 'Ζήτησε νέο σύνδεσμο' : 'Request New Link'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-misel-bg flex items-center justify-center p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="flex justify-between items-start mb-8">
          <Link to="/login" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
            <ArrowLeft size={20} />
            <span className="font-roboto font-bold tracking-wider">MISEL GROUP DIGITAL LAB</span>
          </Link>
          <LanguageToggle />
        </div>

        {success ? (
          <div className="text-center">
            <CheckCircle size={64} className="text-green-500 mx-auto mb-6" />
            <h1 className="font-roboto font-bold text-2xl text-white mb-4">
              {i18n.language === 'el' ? 'Κωδικός Άλλαξε' : 'Password Changed'}
            </h1>
            <p className="text-gray-400 mb-4">
              {i18n.language === 'el' 
                ? 'Ο κωδικός σου άλλαξε με επιτυχία. Θα μεταφερθείς στη σύνδεση...'
                : 'Your password has been changed successfully. Redirecting to login...'
              }
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-roboto font-bold text-2xl text-white mb-2">
              {i18n.language === 'el' ? 'Νέος Κωδικός' : 'New Password'}
            </h1>
            <p className="text-gray-400 mb-8">
              {i18n.language === 'el' 
                ? 'Εισάγετε τον νέο σας κωδικό'
                : 'Enter your new password'
              }
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm text-gray-400">
                  {i18n.language === 'el' ? 'Νέος Κωδικός' : 'New Password'}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-primary"
                    placeholder="••••••••"
                    required
                    minLength={6}
                    data-testid="reset-password"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-gray-400">
                  {i18n.language === 'el' ? 'Επιβεβαίωση Κωδικού' : 'Confirm Password'}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-primary"
                    placeholder="••••••••"
                    required
                    data-testid="reset-confirm"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="reset-submit"
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold tracking-wide uppercase py-6"
              >
                {loading 
                  ? (i18n.language === 'el' ? 'Αποθήκευση...' : 'Saving...') 
                  : (i18n.language === 'el' ? 'Αλλαγή Κωδικού' : 'Change Password')
                }
              </Button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
